import { Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import {
  FUND_LITERATURE,
  MONTH_END_RETURN_NAV,
  PERFORMANCE_MARKET_ETF,
  SESSION_STORAGE_CONSTANTS_CAAS_CONTENT,
} from '../../../constants/constant.content'
import { AssetClassModel } from '../../shared/asset-class-model'
import { FilterModel } from '../../shared/filter-model'
import { ProductTypeModel } from '../../shared/product-type-model'
import { ObservableService } from '../observable-service/observable.service'
import { FilterSelectionInterpreterUtility } from './filter-selection-interpreter-utility'
import { FilterStateSyncupProcess } from './filter-state-syncup-process'
import { FilterNode } from './models/filter-node'

@Injectable({
  providedIn: 'root',
})
export class FundFilterStateInitializer {
  private static URL_PARAM_NM_FILTER = 'filters'
  private static URL_PARAM_NM_VIEW_TYPE = 'viewType'
  private static URL_PARAM_NM_SHARE_CLASS = 'shareClass'
  private static URL_PARAM_NM_BENCHMARK = 'benchmark'
  private static URL_PARAM_VALUE_TRUE_TEXT = 'TRUE'
  private static URL_PARAM_VALUE_TRUE_CHAR = 'T'
  assetClassModel: AssetClassModel = new AssetClassModel()
  productTypeModel: ProductTypeModel = new ProductTypeModel()
  assetCaas: any

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly filterModel: FilterModel,
    private readonly observableService: ObservableService,
    private readonly filterStateSyncupProcess: FilterStateSyncupProcess,
  ) {
    const aemCassDataFromSession = sessionStorage.getItem(
      SESSION_STORAGE_CONSTANTS_CAAS_CONTENT,
    )
    const parsedCaasJson = JSON.parse(aemCassDataFromSession)
    if (parsedCaasJson.data.fd3filtersList) {
      this.assetCaas = parsedCaasJson.data.fd3filtersList.items[0]
    }
  }

  initializeState() {
    const filterState: any = this.readFilterStateFromUrlParam()
    this.populateFilterStateModel(filterState.filterSelection)
    this.populateFilterFlag(filterState)
  }

  private populateFilterFlag(filterState: any) {
    const isShareClassDisplay: boolean =
        filterState.shareClassDisplay &&
        (filterState.shareClassDisplay.toUpperCase() ===
          FundFilterStateInitializer.URL_PARAM_VALUE_TRUE_TEXT ||
          filterState.shareClassDisplay.toUpperCase() ===
            FundFilterStateInitializer.URL_PARAM_VALUE_TRUE_CHAR),
      isBenchMarkDisplay: boolean =
        filterState.benchmarkDisplay &&
        (filterState.benchmarkDisplay.toUpperCase() ===
          FundFilterStateInitializer.URL_PARAM_VALUE_TRUE_TEXT ||
          filterState.benchmarkDisplay.toUpperCase() ===
            FundFilterStateInitializer.URL_PARAM_VALUE_TRUE_CHAR)
    this.observableService.changeSharedClassSelection(isShareClassDisplay)
    this.observableService.changeBenchmarkSelection(isBenchMarkDisplay)
    this.filterStateSyncupProcess.eventLatch = true
    this.observableService.changeViewTypeValue(filterState.viewTypeSelection)
  }

  populateFilterStateModel(filterStateAry: string[]) {
    let filterSet: Set<string>
    if (filterStateAry && filterStateAry.length > 0) {
      filterSet = new Set<string>(filterStateAry)
      this.assetClassModel.isFilterByLevel1Clicked = this.setFilterInModel(
        this.assetClassModel.filterByLevel1,
        filterSet,
      )
      this.productTypeModel.isProductTypeClicked = this.setFilterInModel(
        this.productTypeModel.productType,
        filterSet,
      )
      this.filterModel.editDataFilterBy(this.assetClassModel)
      this.filterModel.editDataProductType(this.productTypeModel)
      this.observableService.viewFilterByClick(
        this.getSelectedAssetClassName(this.assetClassModel),
      )
      this.observableService.viewProductClick(this.getProductType(filterSet))
    }
  }

  getSelectedAssetClassName(assetClass: AssetClassModel) {
    let label = ''
    for (const key in assetClass.filterByLevel1) {
      if (assetClass.filterByLevel1[key].value) {
        label = assetClass.filterByLevel1[key].text
        //Added this logic for sub asset selection count
        let subAssetSelectionCount = 0
        for (const key2 in assetClass.filterByLevel1[key].level2) {
          if (assetClass.filterByLevel1[key].level2[key2].value) {
            subAssetSelectionCount++
          }
        }
        this.filterModel.setSubAssetSelectedCount(subAssetSelectionCount)
      }
    }
    return label
  }
  setFilterInModel(filterModel: any, filterSet: Set<string>): boolean {
    const nodeContainer: FilterNode = new FilterNode({})
    let isAnyFilterSelected = false
    nodeContainer.addChildren(
      FilterSelectionInterpreterUtility.generateTreeStruct(filterModel),
    )
    //     Marking the given filter selection state
    FilterSelectionInterpreterUtility.traverseTree(
      nodeContainer,
      (filterNode: FilterNode) => {
        if (filterSet.has(this.upperCase(filterNode.readData().id))) {
          filterNode.readData().objRef.value = true
          this.setFiltersInParentChain(filterNode)
          isAnyFilterSelected = true
        }
      },
    )
    return isAnyFilterSelected
  }

  private upperCase(strArg: string) {
    return strArg ? strArg.toUpperCase() : strArg
  }

  setFiltersInParentChain(filterNode: FilterNode) {
    let nodePointer: FilterNode = filterNode
    while (nodePointer && nodePointer.readData().ruleName) {
      nodePointer.readData().objRef.value = true
      nodePointer = nodePointer.getParentNode()
    }
  }

  readFilterStateFromUrlParam(): any {
    let filterSelectionStr = this.activatedRoute.snapshot.queryParamMap.get(
      FundFilterStateInitializer.URL_PARAM_NM_FILTER,
    )
    // Added for bookmark issue
    filterSelectionStr = this.getFilterSectionStringBookMark(filterSelectionStr)
    const shareClassDisplay = this.activatedRoute.snapshot.queryParamMap.get(
      FundFilterStateInitializer.URL_PARAM_NM_SHARE_CLASS,
    )
    const benchmarkDisplay = this.activatedRoute.snapshot.queryParamMap.get(
      FundFilterStateInitializer.URL_PARAM_NM_BENCHMARK,
    )
    const viewTypeSelection = this.getViewType()

    return {
      filterSelection: filterSelectionStr?.toUpperCase().split(',') || [],
      viewTypeSelection: viewTypeSelection || MONTH_END_RETURN_NAV,
      shareClassDisplay,
      benchmarkDisplay,
    }
  }

  getViewType() {
    const viewTypeSelection = this.activatedRoute.snapshot.queryParamMap.get(
      FundFilterStateInitializer.URL_PARAM_NM_VIEW_TYPE,
    )
    return viewTypeSelection === MONTH_END_RETURN_NAV ||
      viewTypeSelection === FUND_LITERATURE ||
      viewTypeSelection === PERFORMANCE_MARKET_ETF
      ? viewTypeSelection
      : MONTH_END_RETURN_NAV
  }

  getProductType(filterSet: Set<string>) {
    let productType = this.assetCaas.allFunds
    if (
      filterSet.has(this.upperCase(this.productTypeModel.productType.etf.id))
    ) {
      productType = this.assetCaas.etf
    } else if (
      filterSet.has(this.upperCase(this.productTypeModel.productType.mf.id))
    ) {
      productType = this.assetCaas.mutualFunds
    } else if (
      filterSet.has(
        this.upperCase(this.productTypeModel.productType.colTrust.id),
      )
    ) {
      productType = this.assetCaas.trusts
    }
    return productType
  }

  hasMoreThanOneItemInAnotherArray<T>(array1: T[], array2: T[]): boolean {
    let count = 0
    for (const item of array1) {
      if (array2.includes(item)) {
        count++
        if (count > 1) {
          return true
        }
      }
    }
    return false
  }

  getFilterSectionStringBookMark(filterSelectionStr: string) {
    const productArray = Object.values(this.productTypeModel.productType).map(
      (item) => {
        return item.id
      },
    )
    const assetClassArray = Object.values(
      this.assetClassModel.filterByLevel1,
    ).map((item) => {
      return item.id
    })
    if (filterSelectionStr != null && filterSelectionStr.length > 0) {
      const filterSelectionArray = filterSelectionStr.split(',')
      if (
        this.hasMoreThanOneItemInAnotherArray(
          productArray,
          filterSelectionArray,
        ) ||
        this.hasMoreThanOneItemInAnotherArray(
          assetClassArray,
          filterSelectionArray,
        )
      ) {
        filterSelectionStr = ''
      }
      return filterSelectionStr
    }
  }
}
